import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Camera, Clock, MapPin, Star, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [snakes, setSnakes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSnakes = async () => {
      try {
        const response = await fetch(`${API}/snakes`);
        const data = await response.json();
        setSnakes(data);
      } catch (error) {
        console.error('Error fetching snakes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSnakes();
  }, []);

  const features = [
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Professional Shoots",
      description: "Work with experienced photographers and handlers"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Flexible Scheduling",
      description: "Book by the hour, any day of the week"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Your Choice",
      description: "Studio, outdoor, or your own location"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safe & Professional",
      description: "All snakes are trained and handled safely"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1605022077689-6a40e98fc862?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwbW9kZWwlMjBob2xkaW5nJTIwc25ha2UlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzY3NDkzOTAwfDA&ixlib=rb-4.1.0&q=85"
            alt="Snake Photoshoot"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-primary uppercase tracking-[0.3em] text-sm mb-6"
          >
            Exotic Snake Photography
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6"
          >
            <span className="gradient-text">Slither Sessions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto"
          >
            Create stunning, one-of-a-kind photos with our exotic snake collection. 
            Professional handling, beautiful backdrops, unforgettable memories.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              onClick={() => navigate('/booking')}
              className="btn-primary text-lg px-10 py-6 gold-glow"
              data-testid="hero-book-btn"
            >
              Book Your Session
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/gallery')}
              variant="outline"
              className="btn-secondary text-lg px-10 py-6"
              data-testid="hero-gallery-btn"
            >
              View Gallery
            </Button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-primary rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary uppercase tracking-widest text-sm mb-4">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              The Premier Snake<br />Photoshoot Experience
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-card/50 backdrop-blur-md border-white/10 h-full card-hover" data-testid={`feature-card-${index}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Snake Collection Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary uppercase tracking-widest text-sm mb-4">Our Collection</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Meet Our Exotic Stars
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 rounded-xl image-loading" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {snakes.slice(0, 6).map((snake, index) => (
                <motion.div
                  key={snake.snake_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card 
                    className="snake-card overflow-hidden bg-card border-white/10 card-hover cursor-pointer"
                    onClick={() => navigate('/booking')}
                    data-testid={`snake-card-${snake.snake_id}`}
                  >
                    <div className="relative h-80">
                      <img
                        src={snake.image_url}
                        alt={snake.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                        <div className="flex justify-between items-end">
                          <div>
                            <h3 className="text-xl font-semibold text-white">{snake.name}</h3>
                            <p className="text-white/70 text-sm">{snake.length_ft}ft • {snake.color}</p>
                          </div>
                          <div className="price-tag px-3 py-1 rounded-full text-sm">
                            ${snake.hourly_rate}/hr
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/booking')}
              className="btn-primary"
              data-testid="view-all-snakes-btn"
            >
              View All & Book Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-24 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary uppercase tracking-widest text-sm mb-4">Transparent Pricing</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Simple Hourly Rates
            </h2>
            <p className="text-muted-foreground text-lg">
              No hidden fees. Pay only for what you need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-card/50 backdrop-blur-md border-white/10 p-8" data-testid="pricing-snakes">
              <h3 className="text-2xl font-semibold mb-6 gradient-text">Snake Rentals</h3>
              <ul className="space-y-4">
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Snakes under 5ft</span>
                  <span className="text-primary font-semibold">$40/hr</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Snakes 5ft and up</span>
                  <span className="text-primary font-semibold">$50/hr</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Each additional snake (under 5ft)</span>
                  <span className="text-primary font-semibold">+$5/hr</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Each additional snake (5ft+)</span>
                  <span className="text-primary font-semibold">+$10/hr</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/10 p-8" data-testid="pricing-extras">
              <h3 className="text-2xl font-semibold mb-6 gradient-text">Add-Ons</h3>
              <ul className="space-y-4">
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Studio Rental</span>
                  <span className="text-primary font-semibold">$60/hr</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Transportation</span>
                  <span className="text-primary font-semibold">$25/trip</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-muted-foreground">Member Discount</span>
                  <span className="text-secondary font-semibold">20% off first shoot</span>
                </li>
              </ul>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">
              Members can add guests and split costs evenly!
            </p>
            <Button
              onClick={() => navigate('/register')}
              variant="outline"
              className="btn-secondary"
              data-testid="become-member-btn"
            >
              Become a Member
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 opacity-50" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Ready to Create<br />Something Extraordinary?
          </h2>
          <p className="text-muted-foreground text-lg mb-10">
            Book your exotic snake photoshoot today and capture unforgettable moments.
          </p>
          <Button
            onClick={() => navigate('/booking')}
            className="btn-primary text-lg px-12 py-6 gold-glow"
            data-testid="cta-book-btn"
          >
            Start Booking
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-2xl font-bold gradient-text font-['Playfair_Display']">
                Slither Sessions
              </span>
              <p className="text-muted-foreground text-sm mt-2">
                Exotic Snake Photography Experiences
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/photographer/login" className="text-muted-foreground hover:text-primary text-sm" data-testid="footer-photographer-link">
                Photographer Portal
              </Link>
              <Link to="/gallery" className="text-muted-foreground hover:text-primary text-sm">
                Gallery
              </Link>
              <Link to="/booking" className="text-muted-foreground hover:text-primary text-sm">
                Book Now
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-muted-foreground text-sm">
            © {new Date().getFullYear()} Slither Sessions. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
